import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, HelmetSafety, House, Road, Tree, Droplet, SprayCan, Trowel, Briefcase } from '@/components/ui/Icons';
import { services } from '@/data/services';
import { Picture } from '@/components/ui/Picture';

const ICONS: Record<string, typeof HelmetSafety> = {
  'roofing-services': HelmetSafety,
  'new-roofs': House,
  'roof-repairs': Trowel,
  'roofline-services': Briefcase,
  'damp-proofing': Droplet,
  'driveway-services': Road,
  'landscaping': Tree,
  'spray-foam-removal': SprayCan,
};

export function ServiceGrid() {
  return (
    <section className="bg-paper-50">
      <div className="container-page py-20 sm:py-24">
        <header className="mx-auto mb-14 max-w-2xl text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-gold-700">What we do</p>
          <h2 className="mt-3 font-display text-4xl font-semibold leading-[1.05] text-navy-900 sm:text-5xl/none">
            Interior &amp; exterior contracting services
          </h2>
          <p className="mt-5 text-base leading-relaxed text-ink-700 sm:text-lg/7">
            From a single chimney repair to a full landscaping transformation, we handle
            every part of the job in-house.
          </p>
        </header>

        <ul className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s, i) => {
            const Icon = ICONS[s.slug] ?? Briefcase;
            return (
              <motion.li
                key={s.slug}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 0.45, delay: 0.04 * (i % 3), ease: [0.22, 1, 0.36, 1] }}
              >
                <Link
                  to={`/${s.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-xl border border-paper-100 bg-paper-0 shadow-card transition duration-300 hover:-translate-y-1 hover:border-gold-300 hover:shadow-[0_24px_48px_-24px_rgba(7,15,31,0.4)]"
                >
                  <div className="relative aspect-16/10 overflow-hidden bg-navy-100">
                    <Picture
                      src={`/images/services/${s.slug}.jpg`}
                      alt=""
                      className="block h-full w-full"
                      imgClassName="h-full w-full object-cover transition duration-700 ease-out group-hover:scale-105"
                      hideOnError
                    />
                    <div className="absolute inset-0 bg-linear-to-t from-navy-900/80 via-navy-900/10 to-transparent" />
                    <div className="absolute left-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-gold-400/95 text-navy-900 shadow-lg ring-1 ring-gold-300/50">
                      <Icon className="h-4 w-4" />
                    </div>
                    <h3 className="absolute bottom-4 left-4 right-4 font-display text-2xl font-semibold leading-tight text-paper-0">
                      {s.navLabel}
                    </h3>
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <p className="text-sm leading-relaxed text-ink-700">{s.short}</p>
                    <span className="mt-auto inline-flex items-center gap-1.5 pt-5 text-sm font-semibold text-gold-700 group-hover:text-gold-600">
                      Explore service
                      <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
